import React, { useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import * as THREE from 'three'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Nhãn vec tơ: chữ in nghiêng + mũi tên vec tơ vẽ bằng SVG phía trên (không phụ thuộc font KaTeX)
function VecLabel({ sym, color }: { sym: string; color: string }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', color, fontWeight: 700, fontStyle: 'italic', fontSize: 18, lineHeight: 1, paddingTop: 7, textShadow: '0 0 3px #fff, 0 0 3px #fff' }}>
      <span style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1.3em', height: 6 }}>
        <svg viewBox="0 0 26 6" width="100%" height="6" style={{ display: 'block', overflow: 'visible' }}>
          <line x1="1" y1="3" x2="23" y2="3" stroke={color} strokeWidth="2" />
          <path d="M23 3 L18 0.6 M23 3 L18 5.4" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      </span>
      {sym}
    </span>
  )
}

// hl/hw = tỉ lệ đầu mũi tên so với chiều dài; origin = gốc mũi tên (để kéo dài đuôi)
function Arrow({ dir, length, color, hl = 0.22, hw = 0.14, origin }: { dir: THREE.Vector3; length: number; color: number; hl?: number; hw?: number; origin?: THREE.Vector3 }) {
  const helper = useMemo(() => {
    const o = origin ? origin.clone() : new THREE.Vector3(0, 0, 0)
    const h = new THREE.ArrowHelper(dir.clone().normalize(), o, length, color, length * hl, length * hw)
      ; (h.line as any).material.linewidth = 3
    return h
  }, [dir, length, color, hl, hw, origin])
  return <primitive object={helper} />
}

// Cung tròn chỉ góc θ giữa B và đoạn dây (trục x), trong mặt phẳng x-y. `end` = góc thực của B (có dấu)
function AngleArc({ end }: { end: number }) {
  const geo = useMemo(() => {
    const r = 0.28, seg = 40, pts: THREE.Vector3[] = []
    for (let i = 0; i <= seg; i++) { const a = (end * i) / seg; pts.push(new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, 0)) }
    return new THREE.BufferGeometry().setFromPoints(pts)
  }, [end])
  return <line>{<primitive object={geo} attach="geometry" />}<lineBasicMaterial color={0xf59e0b} /></line>
}

function Scene({ I, B, angle, bSign }: { I: number; B: number; angle: number; bSign: number }) {
  const rad = (angle * Math.PI) / 180
  const s = Math.sign(I) || 1
  const Idir = new THREE.Vector3(s, 0, 0)
  const Bdir = new THREE.Vector3(Math.cos(rad) * bSign, Math.sin(rad) * bSign, 0)  // B tạo góc `angle` với đoạn dây (đổi chiều khi bSign=-1)
  const bEnd = Math.atan2(Bdir.y, Bdir.x)                          // góc thực của B (có dấu) để vẽ cung θ
  const Fdir = Idir.clone().cross(Bdir)                            // F = I L × B → (0,0, s·sinθ)
  const sinT = Math.abs(Math.sin(rad))
  const lenI = 1 + Math.abs(I) * 0.25
  const lenB = 0.8 + B * 0.12
  const back = 0.55                                                // đoạn kéo dài đuôi B về phía ngược lại
  const lenF = Math.abs(I) * B * sinT * 0.12   // F = B·I·L·sinθ → 0 khi B ∥ đoạn dây
  const showF = lenF > 0.08

  // lưới vec tơ B phủ toàn bộ đoạn dây (theo trục x) + 3 lớp theo z
  const bGrid: [number, number, number][] = []
  for (const x of [-1.6, -0.8, 0, 0.8, 1.6]) for (const z of [-0.8, 0, 0.8]) bGrid.push([x, 0, z])
  const bOrigin = Bdir.clone().multiplyScalar(-back)

  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[4, 6, 5]} intensity={0.8} />
      {/* Dây dẫn (theo trục x) */}
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.08, 0.08, 4, 20]} />
        <meshStandardMaterial color="#94a3b8" metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Vector I (xanh dương) */}
      <Arrow dir={Idir} length={lenI} color={0x2563eb} />
      <Html position={[Idir.x * (lenI + 0.3), 0.5, 0]} center><VecLabel sym="I" color="#2563eb" /></Html>
      {/* Vector B (xanh lá) — phủ kín đoạn dây, đuôi kéo dài về phía ngược lại, đầu mũi tên nhỏ */}
      {bGrid.map((p, i) => (
        <group key={i} position={p}><Arrow dir={Bdir} length={lenB + back} color={0x10b981} hl={0.13} hw={0.085} origin={bOrigin} /></group>
      ))}
      <Html position={[Bdir.x * (lenB + 0.4), Bdir.y * (lenB + 0.4), 0.8]} center><VecLabel sym="B" color="#10b981" /></Html>
      {/* Cung góc θ giữa B và đoạn dây */}
      <AngleArc end={bEnd} />
      <Html position={[Math.cos(bEnd / 2) * 0.4, Math.sin(bEnd / 2) * 0.4, 0]} center><span style={{ color: '#d97706', fontWeight: 700, fontSize: 13 }}>θ</span></Html>
      {/* Vector F (tím) — đầu mũi tên nhỏ */}
      {showF && <Arrow dir={Fdir} length={lenF} color={0x7c3aed} hl={0.16} hw={0.1} />}
      {showF && <Html position={[Fdir.x * lenF, Fdir.y * lenF + 0.1, Fdir.z * lenF + 0.28]} center><VecLabel sym="F" color="#7c3aed" /></Html>}
      <gridHelper args={[8, 8, 0xcbd5e1, 0xe2e8f0]} position={[0, -2, 0]} />
      <OrbitControls enablePan={false} />
    </>
  )
}

// Lực từ tác dụng lên dây dẫn (Lớp 12 — Bài 13): F = B·I·L·sinθ, phương vuông góc với cả I và B
// (quy tắc bàn tay trái). Xoay chuột để quan sát 3 vector trong không gian 3D.
const MagneticForceSimulation: React.FC<Props> = ({
  title = 'Lực từ tác dụng lên dây dẫn (3D)',
  description = 'F = B·I·L·sinθ. Lực vuông góc với cả dòng điện I và cảm ứng từ B (quy tắc bàn tay trái). Chỉnh góc θ giữa B và đoạn dây để thấy F thay đổi. Kéo chuột để xoay mô hình.',
  onComplete,
}) => {
  const [I, setI] = React.useState(3)
  const [B, setB] = React.useState(3)
  const [angle, setAngle] = React.useState(90)
  const [bSign, setBSign] = React.useState(1)
  const firstRef = React.useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }
  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="rounded-lg overflow-hidden border border-gray-100" style={{ height: 320, background: 'linear-gradient(#f8fafc,#eef2f7)' }}>
        <Canvas camera={{ position: [4, 3, 5], fov: 50 }} onCreated={touch}>
          <Scene I={I} B={B} angle={angle} bSign={bSign} />
        </Canvas>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Dòng điện I</span><input type="range" min={-5} max={5} step={1} value={I} onChange={(e) => { touch(); setI(Number(e.target.value)) }} className="flex-1" /><span className="w-10 text-right font-semibold">{I > 0 ? '+' : ''}{I}</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Cảm ứng từ B</span><input type="range" min={1} max={5} step={1} value={B} onChange={(e) => setB(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{B}</span></label>
        <label className="flex items-center gap-2 col-span-2"><span className="w-28 text-gray-600">Góc θ (B, dây)</span><input type="range" min={0} max={180} step={5} value={angle} onChange={(e) => { touch(); setAngle(Number(e.target.value)) }} className="flex-1 accent-violet-500" /><span className="w-12 text-right font-semibold">{angle}°</span></label>
      </div>
      <button onClick={() => { touch(); setBSign((x) => -x) }} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 font-bold text-sm hover:bg-emerald-50">⇄ Đổi chiều B</button>
      <p className="text-[11px] text-gray-400 mt-2">🔵 I (dòng điện) · 🟢 B (cảm ứng từ) · 🟣 F (lực từ). Đổi chiều I → F đảo chiều; θ = 0° hoặc 180° → F = 0.</p>
    </div>
  )
}
export default MagneticForceSimulation
